import React, {useEffect, useState} from 'react';
import './index.scss';
import {Helmet} from 'react-helmet';
import DashboardHeader from '../../components/DashboardHeader';
import Wrapper from '../../components/Wrapper';
import Input from '../../components/Input';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import InfoIcon from '../../SVG/InfoIcon';
import SimpleModal from '../../components/SimpleModal';
import Datepicker from '../../components/Datepicker';
import moment from 'moment/moment';
import {customRows} from './customInspectionsUtils';
import Trash from '../../SVG/Trash';
import Pencil from '../../SVG/Pencil';
import Monitor from '../../SVG/Monitor';
import {
  setActiveInspection,
  setEditInspection,
} from '../../store/builder/inspectionSlice';
import {setLoader} from '../../store/builder/reducer';
import {
  useDeleteCustomInspectionMutation,
  useExtendCustomInspectionMutation,
  useLazyGetCustomFormsInspectionsQuery,
  usePublishCustomInspectionMutation,
  useUpdateOpenDaysMutation,
} from '../../store/builder/inspections.api';
import {useDebounce} from '../LogSheets/hooks';
import {toast} from 'react-toastify';
import {useErrorHandler} from '../../global/ErrorsHandler';
import CustomPaginate from '../../components/PaginateComponent';
import useLoader from '../../global/UseLoadingHandler';
import {useTranslation} from '../../hooks/useTranslation';
import Settings from '../../SVG/Settings';

const CustomInspections = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userType =
    location.state?.userType === 'corporate' ? 'corporate' : 'admin';
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const [extendModal, setExtendModal] = useState(false);
  const [extendDate, setExtendDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [chosenInspection, setChosenInspection] = useState(null);
  const [search, setSearch] = useState('');
  const [publishStatus, setPublishStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearchValue = useDebounce(search, 500);
  const [openDaysModal, setOpenDaysModal] = useState(false);
  const [openDays, setOpenDays] = useState(0);
  const [
    getCustoms,
    {
      data: getCustomsData,
      isError: getCustomsIsError,
      isLoading: getCustomsIsLoading,
      error: getCustomsError,
      isSuccess: getCustomsIsSuccess,
    },
  ] = useLazyGetCustomFormsInspectionsQuery();

  const [
    publishInspection,
    {
      data: publishInspectionData,
      isError: publishInspectionIsError,
      isLoading: publishInspectionIsLoading,
      error: publishInspectionError,
      isSuccess: publishInspectionIsSuccess,
    },
  ] = usePublishCustomInspectionMutation();

  const [
    extendInspection,
    {
      data: extendInspectionData,
      isError: extendInspectionIsError,
      isLoading: extendInspectionIsLoading,
      error: extendInspectionError,
      isSuccess: extendInspectionIsSuccess,
    },
  ] = useExtendCustomInspectionMutation();

  const [
    removeInspection,
    {
      data: removeInspectionData,
      isError: removeInspectionIsError,
      isLoading: removeInspectionIsLoading,
      error: removeInspectionError,
      isSuccess: removeInspectionIsSuccess,
    },
  ] = useDeleteCustomInspectionMutation();

  const [
    updateOpenDaysAPI,
    {
      data: updateOpenDaysData,
      isError: updateOpenDaysIsError,
      isLoading: updateOpenDaysIsLoading,
      error: updateOpenDaysError,
      isSuccess: updateOpenDaysIsSuccess,
    },
  ] = useUpdateOpenDaysMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    getCustomsError,
    publishInspectionError,
    extendInspectionError,
    removeInspectionError,
    updateOpenDaysError,
  ]);

  const getInspectionsList = () => {
    if (userType === 'corporate') {
      getCustoms({
        page: currentPage,
        search: debouncedSearchValue,
        corporate: true,
      });
    } else {
      getCustoms({page: currentPage, search: debouncedSearchValue});
    }
  };

  useLoader([
    removeInspectionIsLoading,
    getCustomsIsLoading,
    publishInspectionIsLoading,
    extendInspectionIsLoading,
    getCustomsIsLoading,
    updateOpenDaysIsLoading,
  ]);

  useEffect(() => {
    if (getCustomsData?.data?.length > 0) {
      setRows(
        getCustomsData?.data?.map(old => ({...old, action_status: false})),
      );
    } else {
      setRows([]);
    }
  }, [getCustomsData]);

  useEffect(() => {
    getInspectionsList();
  }, [debouncedSearchValue, userType, currentPage]);

  useEffect(() => {
    if (updateOpenDaysIsSuccess) {
      toast.success(t('customInspections.messages.openDaysUpdatedSuccess'));
      setOpenDaysModal(false);
      setOpenDays(0);
      getInspectionsList();
    }
  }, [updateOpenDaysIsSuccess]);

  useEffect(() => {
    setRows([]);
    return window.addEventListener('click', e => {
      if (
        e.target.closest('.reports__settings') === null &&
        e.target.closest('.custom__table__dropdown') === null
      ) {
        setRows(old => {
          let data = old.map(item => {
            return {...item, action_status: false};
          });
          return data;
        });
      }
    });
  }, [location]);

  const handleSearch = e => {
    setSearch(e.target.value);
  };

  const editInspection = (e, item) => {
    e.stopPropagation();
    dispatch(setEditInspection(item));
    if (userType === 'corporate') {
      navigate(`/corporate/custom-inspections/edit-inspection/${item?.id}`, {
        state: {userType: userType},
      });
    } else {
      navigate(`/custom-inspections/edit-inspection/${item?.id}`, {
        state: {
          userType: userType,
          readOnlyFromCorporate:
            item?.parent_id && userType === 'admin' ? true : false,
        },
      });
    }
  };

  const assignInspection = (e, item) => {
    e.stopPropagation();
    dispatch(setEditInspection(item));
    if (userType === 'corporate') {
      navigate(
        `/corporate/custom-inspections/edit-inspection/${item?.id}?assign=true`,
        {state: {userType: userType}},
      );
    } else {
      navigate(`/custom-inspections/edit-inspection/${item?.id}?assign=true`, {
        state: {userType: userType},
      });
    }
  };

  const clickSettings = (e, row, v) => {
    e.stopPropagation();
    setRows(old => {
      let data = old.map(item => {
        if (item?.id === row?.id) {
          return {...item, action_status: v};
        } else {
          return {...item, action_status: false};
        }
        return item;
      });
      return data;
    });
  };

  const handleRow = (e, item) => {
    if (userInfo?.is_corporate) {
      e.stopPropagation();
      dispatch(setActiveInspection(item));
    }
  };

  const submitPublish = () => {
    publishInspection(chosenInspection)
      .unwrap()
      .then(res => {
        getInspectionsList();
        toast.success(t('customInspections.messages.inspectionPublishedSuccess'));
        setPublishStatus(false);
      })
      .catch(err => {
        setPublishStatus(false);
      });
  };

  const submitExtend = () => {
    if (extendDate !== null) {
      extendInspection({id: chosenInspection, body: {date: extendDate}})
        .unwrap()
        .then(res => {
          getInspectionsList();
          toast.success(t('customInspections.messages.inspectionDateExtendedSuccess'));
          setExtendModal(false);
        })
        .catch(err => {
          setExtendModal(false);
        });
    } else {
      toast.error(t('customInspections.messages.chooseDate'));
    }
  };

  const deleteCustomInspectionAction = () => {
    removeInspection(chosenInspection)
      .unwrap()
      .then(res => {
        getInspectionsList();
        toast.success(t('customInspections.messages.inspectionDeletedSuccess'));
        setDeleteModal(false);
      })
      .catch(err => {
        setDeleteModal(false);
      });
  };

  const renderDeleteModal = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('customInspections.modals.deleteTitle')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setDeleteModal(false)}
            />
          </div>
          <div
            className='modal__content-body'
            style={{display: 'flex', justifyContent: 'center'}}
          >
            {t('customInspections.modals.deleteMessage')}
          </div>
          <div className='modal__content-bottom'>
            <button
              className='save danger'
              onClick={deleteCustomInspectionAction}
            >
              {t('actions.delete')}
            </button>
            <button
              className='close almost'
              onClick={() => setDeleteModal(false)}
            >
              {t('actions.cancel')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const renderPublishModal = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('customInspections.modals.publishTitle')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setPublishStatus(false)}
            />
          </div>
          <div
            className='modal__content-body'
            style={{display: 'flex', justifyContent: 'center'}}
          >
            {t('customInspections.modals.publishMessage')}
          </div>
          <div className='modal__content-bottom'>
            <button className='close' onClick={() => setPublishStatus(false)}>
              {t('actions.cancel')}
            </button>
            <button className='save' onClick={submitPublish}>
              {t('customInspections.actions.publish')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const handleOpenDaysSubmit = async () => {
    if (!chosenInspection || !openDays || openDays <= 0) {
      return;
    }
    
    try {
      await updateOpenDaysAPI({
        id: chosenInspection,
        open_days: parseInt(openDays),
      });
    } catch (error) {
      console.error('Error updating open days:', error);
    }
  };

  const handleOpenDaysCancel = () => {
    setOpenDaysModal(false);
    setOpenDays(0); // Reset to default value
  };

  const renderOpenDaysModal = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('customInspections.modals.openDaysTitle')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={handleOpenDaysCancel}
            />
          </div>
          <div className='modal__content-body'>
            <div className='modal__content-body-item'>
              <p className='modal__content-body-label'>{t('customInspections.modals.openDaysLabel')}</p>
              <Input
                type='number'
                placeholder={t('customInspections.modals.openDaysPlaceholder')}
                value={openDays}
                onChange={e => setOpenDays(e.target.value)}
                min='0'
                customStyles={{
                  width: '100%',
                }}
              />
            </div>
          </div>
          <div className='modal__content-bottom'>
            <button 
              className='close' 
              onClick={handleOpenDaysCancel}
            >
              {t('actions.cancel')}
            </button>
            <button 
              className='save' 
              onClick={handleOpenDaysSubmit}
              disabled={!openDays || openDays <= 0}
              style={{backgroundColor: !openDays || openDays <= 0 ? '#908e8e' : '#4268b0'}}
            >
              {t('customInspections.actions.setOpenDays')}
            </button>
          </div>
        </div>
        <div className='modal__bg' onClick={handleOpenDaysCancel}></div>
      </div>
    );
  };

  const handlePageClick = page => {
    setCurrentPage(page.selected + 1);
    window.scrollTo(0, 0);
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('customInspections.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={t('customInspections.title')}
        showBtn={true}
        type='custom-inspections'
        action={() => {
          if (userType === 'corporate') {
            navigate(`/corporate/custom-inspections/build`, {
              state: {userType: 'corporate'},
            });
          } else {
            navigate(`/custom-inspections/build`);
          }
        }}
      />
      {extendModal && (
        <SimpleModal
          title={t('customInspections.modals.extendTitle')}
          exit={() => {
            setExtendModal(false);
          }}
          submitAction={() => submitExtend()}
          submitTitle={t('customInspections.modals.extendButton')}
        >
          <div className='custom__block'>
            <div className='custom__block-item'>
              <p className='custom__block-item__label'>{t('customInspections.modals.extendEndDate')}</p>
              <Datepicker
                minDate={new Date()}
                width={'100%'}
                setValue={e =>
                  setExtendDate(old => moment(e).format('Y-MM-DD'))
                }
              />
            </div>
          </div>
        </SimpleModal>
      )}

      {deleteModal && renderDeleteModal()}
      {publishStatus && renderPublishModal()} 
      {openDaysModal && renderOpenDaysModal()}

      <div className='custom'>
        <div className='custom__content'>
          <div className='custom__search'>
            <Input
              type='text'
              placeholder={t('customInspections.searchPlaceholder')}
              name='custom_name'
              id='custom_name'
              image={'/assets/icons/search.svg'}
              maxwidth={264}
              onChange={handleSearch}
            />
          </div>
          <div className='custom__table'>
            <div
              className={`custom__table-header ${userType === 'corporate' ? 'custom__table-header-isCorporate' : ''}`}
            >
              <div className='custom__table-th'>{t('customInspections.tableHeaders.customInspections')}</div>
              <div className='custom__table-th'>{t('customInspections.tableHeaders.period')}</div>
              <div className='custom__table-th'>{t('customInspections.tableHeaders.openDays')}</div>
              {userType !== 'corporate' && (
                <>
                  <div className='custom__table-th'>{t('customInspections.tableHeaders.type')}</div>
                  <div className='custom__table-th'>{t('customInspections.tableHeaders.startDate')}</div>
                  <div className='custom__table-th'>{t('customInspections.tableHeaders.forms')}</div>
                </>
              )}
              <div className='custom__table-th'>{t('customInspections.tableHeaders.dateCreated')}</div>
              <div className='custom__table-th'>{t('customInspections.tableHeaders.dateModified')}</div>
              {userType !== 'corporate' && (
                <>
                  <div className='custom__table-th'>{t('customInspections.tableHeaders.createdBy')}</div>
                  <div className='custom__table-th'>{t('customInspections.tableHeaders.lastModifiedBy')}</div>
                </>
              )}
              {userType === 'corporate' && (
                <>
                  <div className='custom__table-th'>{t('customInspections.tableHeaders.goLiveDate')}</div>
                  <div className='custom__table-th'>{t('customInspections.tableHeaders.publishWithFBOs')}</div>
                </>
              )}
              <div className='custom__table-th'></div>
            </div>
            <div className='custom__table-body'>
              {rows?.length > 0 ? (
                rows?.map((row, index) => (
                  <div
                    onClick={e => handleRow(e, row)}
                    key={index}
                    style={{position: 'relative'}}
                  >
                    <div
                      className={`custom__table-row ${userType === 'corporate' ? 'custom__table-row-isCorporate' : ''}`}
                    >
                      <div className='custom__table-bodyTh'>{row?.name}</div>
                      <div className='custom__table-bodyTh'>{row?.period}</div>
                      <div className='custom__table-bodyTh'>{row?.open_days}</div>

                      {userType !== 'corporate' && (
                        <>
                          <div className='custom__table-bodyTh'>
                            {userType === 'admin' ? (
                              row?.agency_id == null ? (
                                <button
                                  className='custom-button customGreen small'
                                  onClick={e => assignInspection(e, row)}
                                >
                                  {t('customInspections.actions.assign')}
                                </button>
                              ) : row?.is_for_equipment ? (
                                t('customInspections.actions.equipment')
                              ) : (
                                t('customInspections.actions.facility')
                              )
                            ) : row?.is_for_equipment ? (
                              t('customInspections.actions.equipment')
                            ) : (
                              t('customInspections.actions.facility')
                            )}
                          </div>
                          <div className='custom__table-bodyTh'>
                            {moment.utc(row?.inspection_start_date).format(
                              'MM/DD/YY',
                            )}
                          </div>
                          <div className='custom__table-bodyTh'>
                            {row?.categories_count}
                          </div>
                        </>
                      )}
                      <div className='custom__table-bodyTh'>
                        {moment(row?.created_at).format('MM/DD/YY')}
                      </div>
                      <div className='custom__table-bodyTh'>
                        {moment(row?.updated_at).format('MM/DD/YY')}
                      </div>
                      {userType !== 'corporate' && (
                        <>
                          <div className='custom__table-bodyTh'>
                            {row?.user?.name}
                          </div>
                          <div className='custom__table-bodyTh'>
                            {moment(row?.updated_at).format('MM/DD/YY')}
                          </div>
                        </>
                      )}
                      {userType === 'corporate' && (
                        <>
                          {row?.is_expired ? (
                            <div className='custom__table-bodyTh red-col'>
                              <InfoIcon color={'#DA6767'} />
                              <p>
                                {moment(row?.inspection_start_date).format(
                                  'MM/DD/YY',
                                )}{' '}
                                {t('customInspections.actions.expired')}
                              </p>
                            </div>
                          ) : (
                            <div className='custom__table-bodyTh'>
                              {moment(row?.inspection_start_date).format(
                                'MM/DD/YY',
                              )}
                            </div>
                          )}
                          <div className='custom__table-bodyTh'>
                            {row?.is_published ? (
                              <p className='text-button primary-button light-text'>
                                {t('customInspections.actions.published')}
                              </p>
                            ) : row?.is_expired ? (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setExtendModal(true);
                                  setChosenInspection(row?.id);
                                }}
                                className='custom-button red small'
                              >
                                {t('customInspections.actions.extend')}
                              </button>
                            ) : (
                              <button
                                className='custom-button green small'
                                onClick={() => {
                                  setPublishStatus(true);
                                  setChosenInspection(row?.id);
                                }}
                              >
                                {t('customInspections.actions.publish')}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      <div className='custom__table-bodyTh'>
                        <img
                          className='custom__table-settings'
                          src='/assets/icons/dots.svg'
                          alt='dots'
                          onClick={e =>
                            clickSettings(e, row, !row?.action_status)
                          }
                        />
                      </div>
                    </div>
                    {row?.action_status && (
                      <div className='custom__table__dropdown'>
                        <div
                          className='custom__table__dropdown-elem'
                          onClick={() => {
                            if (userType === 'corporate') {
                              navigate(
                                `/corporate/custom-inspections/details/${row?.id}`,
                              );
                            } else {
                              if (row?.agency_id == null) {
                                navigate(
                                  `/custom-inspections/forms/${row?.id}`,
                                  {
                                    state: {
                                      hiddenBtn: true,
                                      userType: 'admin',
                                    },
                                  },
                                );
                              } else {
                                navigate(
                                  `/custom-inspections/forms/${row?.id}`,
                                  {state: {userType: 'admin'}},
                                );
                              }
                            }
                          }}
                        >
                          <Monitor color={'#AEB3B7'} />
                          <span>{t('customInspections.actions.inspectionDetails')}</span>
                        </div>
                        {(userType === 'admin' || userType === 'global') &&
                        row?.agency_id == null ? null : (
                          <>
                            <div
                              className='custom__table__dropdown-elem'
                              onClick={e => editInspection(e, row)}
                            >
                              <Pencil color={'#999999'} />
                              <span>{t('customInspections.actions.editInspection')}</span>
                            </div>
                              <>
                                <div
                              className='custom__table__dropdown-elem'
                              onClick={() => {
                                setDeleteModal(true);
                                setChosenInspection(row.id);
                              }}
                            >
                              <Trash color={'#999999'} />
                              <span>{t('customInspections.actions.deleteInspection')}</span>
                              </div>
                                { typeof row?.open_days === 'number' && row?.open_days > 0 && (
                                  <>
                              <div
                              className='custom__table__dropdown-elem'
                              onClick={() => {
                                setOpenDaysModal(true);
                                setChosenInspection(row.id);
                                setOpenDays(row?.open_days);
                              }}
                              >
                                <Settings color={'#999999'} />
                              <span>{t('customInspections.actions.setOpenDays')}</span>
                            </div>
                                    </>
                                )}
                              </>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '205px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingTop: 100,
                    paddingBottom: 100,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <div className='expired__box-img' style={{margin: '0'}}>
                      <img src='/assets/no-data.svg' alt='no-data' />
                    </div>
                    <p className='no-payment-text'>{t('customInspections.messages.noData')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <CustomPaginate
            pageCount={getCustomsData?.pages}
            handlePageClick={handlePageClick}
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default CustomInspections;
